/**
 * VoiceLeadEngagementProvider — single owner of the lead-facing WebSocket pair
 * `agent:engaging` / `agent:disengaged` for the VOICE flow (dashboard widget
 * "Call Lead" → dedicated /call-center/call-lead screen).
 *
 * Why this exists: the TwilioProvider is intentionally WebSocket-free, and the
 * dedicated call screen unmounts when the agent navigates away mid-call (the
 * floating ActiveCallIndicator keeps the call alive). If disengaging lived on
 * that screen, a call that ends while the agent is elsewhere would never emit
 * `agent:disengaged`, leaving the lead's IncomingCallScreen stuck. This
 * provider is mounted globally (next to TwilioProvider), so it survives that
 * navigation and observes the whole call lifecycle.
 *
 * Pairing model: `engageLead()` (called from the widget click) emits
 * `agent:engaging` AND arms a pending disengage with that lead's session. When
 * the Twilio call subsequently ends — regardless of which screen is mounted —
 * we emit `agent:disengaged` with the recorded reason and disarm.
 *
 * Surfaces that do NOT call `engageLead()` (the direct-call popup, etc.) stay
 * fully WS-silent: nothing is armed, so nothing is emitted.
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { useTwilioContext } from "@/context/twilio.context";

type EngageLeadArgs = {
  /** The lead's session — the room the backend relays the events to. */
  sessionName: string;
  leadId: number;
  /**
   * The lead's email. Personal-room leads share their socket room, so the
   * backend relays these events to every lead waiting on that room; the
   * lead's portal uses this field to ignore events addressed to someone else.
   */
  leadEmail?: string;
  /** Agent first name, surfaced to the lead's portal in agent:engaging. */
  agentName?: string;
};

type VoiceLeadEngagementContextValue = {
  /**
   * Emit `agent:engaging` for this lead and arm the matching
   * `agent:disengaged`, which fires automatically when the resulting voice
   * call terminates. Call this from the dashboard widget's "Call Lead" click.
   */
  engageLead: (args: EngageLeadArgs) => void;
  /**
   * The sessionName THIS agent is currently engaging via voice, or null. Lets
   * the UI tell "you're calling this lead" apart from "another agent is".
   */
  engagingSessionName: string | null;
};

const VoiceLeadEngagementContext =
  createContext<VoiceLeadEngagementContextValue | null>(null);

export const VoiceLeadEngagementProvider = ({ children }: PropsWithChildren) => {
  const socket = useSocketContext();
  const { state, callEndReason } = useTwilioContext();

  // The lead whose engagement is pending a disengage emit, or null when none
  // is armed. A ref (not state) because the disengage decision is driven by
  // the Twilio state machine below, not by re-renders.
  const engagementRef = useRef<{ sessionName: string; leadId: number; leadEmail?: string } | null>(
    null,
  );
  // State mirror of the engaged sessionName so consumers (the leads widget)
  // re-render and can show "you're calling this lead" for the active agent.
  const [engagingSessionName, setEngagingSessionName] = useState<string | null>(null);
  // Whether the armed call has reached an active state on THIS engagement.
  // Guards against emitting disengaged off a stale terminal state that may
  // linger from a previous call at the moment we arm.
  const sawActiveRef = useRef(false);

  const engageLead = useCallback(
    ({ sessionName, leadId, leadEmail, agentName }: EngageLeadArgs) => {
      engagementRef.current = { sessionName, leadId, leadEmail };
      sawActiveRef.current = false;
      setEngagingSessionName(sessionName);
      socket.emit("agent:engaging", {
        sessionName,
        agentName,
        engagementType: "voice",
        ...(leadEmail ? { email: leadEmail } : {}),
      });
    },
    [socket],
  );

  // Drives `agent:disengaged` off the Twilio call lifecycle. Once a call is
  // armed via engageLead(), we wait for it to go active, then emit when it
  // reaches a terminal state — wherever the agent has navigated by then.
  useEffect(() => {
    if (!engagementRef.current) return;

    const active =
      state === "initializing" ||
      state === "connecting" ||
      state === "ringing" ||
      state === "in-call";
    if (active) {
      sawActiveRef.current = true;
      return;
    }

    const terminal = state === "ended" || state === "error";
    if (terminal && sawActiveRef.current) {
      const { sessionName, leadId, leadEmail } = engagementRef.current;
      socket.emit("agent:disengaged", {
        sessionName,
        leadId,
        reason: callEndReason ?? (state === "error" ? "failed" : "completed"),
        ...(leadEmail ? { email: leadEmail } : {}),
      });
      engagementRef.current = null;
      sawActiveRef.current = false;
      setEngagingSessionName(null);
    }
  }, [state, callEndReason, socket]);

  return (
    <VoiceLeadEngagementContext.Provider value={{ engageLead, engagingSessionName }}>
      {children}
    </VoiceLeadEngagementContext.Provider>
  );
};

export const useVoiceLeadEngagement = (): VoiceLeadEngagementContextValue => {
  const ctx = useContext(VoiceLeadEngagementContext);
  if (!ctx) {
    throw new Error(
      "useVoiceLeadEngagement must be used inside VoiceLeadEngagementProvider",
    );
  }
  return ctx;
};
